import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import type { RaceEvent } from '../types';
import { raceEventService } from '../services/raceService';
import type { GroupRideOccurrenceSummary } from '../services/raceService';
import { storageService } from '../services/storageService';
import FileUpload from '../components/FileUpload';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import GroupRideMap from '../components/GroupRideMap';
import GroupRideElevationChart from '../components/GroupRideElevationChart';
import { MapPinIcon } from '../components/icons/PlaybackIcons';
import { formatTime12h, getTimeZoneAbbreviation } from '../components/AddRaceModal';
import { computeSectorStats } from '../utils/raceSector';
import { trimRouteSegment, getDistanceMeters } from '../utils/routeTrimmer';

const GroupRideDetailsPage: React.FC = () => {
    const { rideId } = useParams<{ rideId: string }>();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const location = useLocation();

    // Context-aware back label
    const backLabel = useMemo(() => {
        const from = (location.state as any)?.from;
        if (from === 'my') return 'Back to My Events';
        if (from === 'find') return 'Back to Find Events';
        if (from === 'profile') return 'Back to Profile';
        if ((location.state as any)?.backLabel) return (location.state as any).backLabel;
        return 'Back';
    }, [location.state]);

    const handleBack = () => {
        if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
            navigate(isMobile ? '/races' : '/');
        }
    };

    const [ride, setRide] = useState<RaceEvent | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'about' | 'host'>('about');
    const [isFavorited, setIsFavorited] = useState(false);
    const [isFavoriting, setIsFavoriting] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [occurrences, setOccurrences] = useState<GroupRideOccurrenceSummary[]>([]);
    const [isLoadingOccurrences, setIsLoadingOccurrences] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    // Always ensure page opens at the top
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [rideId]);

    useEffect(() => {
        const fetchRide = async () => {
            if (!rideId) return;
            setIsLoading(true);
            try {
                const event = await raceEventService.getRaceEventBySlug(rideId);
                if (event) {
                    setRide(event);
                }
            } catch (err) {
                console.error("Failed to fetch group ride details", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchRide();
    }, [rideId]);

    // Load the user's current favorite state for this ride from their profile
    useEffect(() => {
        const checkFavoriteState = async () => {
            if (!currentUser || !rideId) return;
            try {
                const profile = await userService.getUserProfile(currentUser.uid);
                if (profile?.favoriteGroupRides) {
                    const alreadyFavorited = profile.favoriteGroupRides.some(r => r.id === rideId);
                    setIsFavorited(alreadyFavorited);
                }
            } catch (err) {
                console.error("Failed to check favorite state", err);
            }
        };
        checkFavoriteState();
    }, [currentUser, rideId]);

    // Load recent per-date occurrences of this recurring ride
    useEffect(() => {
        if (!ride?.id) return;
        const fetchOccurrences = async () => {
            setIsLoadingOccurrences(true);
            try {
                setOccurrences(await raceEventService.getGroupRideOccurrences(ride.id));
            } finally {
                setIsLoadingOccurrences(false);
            }
        };
        fetchOccurrences();
    }, [ride?.id]);

    const gr = ride?.groupRideDetails;

    const meetupCoords = useMemo(() => {
        const s = ride?.course?.start;
        if (s && Array.isArray(s) && s.length >= 2 && (s[0] !== 0 || s[1] !== 0)) return [Number(s[0]), Number(s[1])] as [number, number];
        if (s && typeof s === 'object') {
            const lat = Number((s as any).lat ?? (s as any).latitude);
            const lon = Number((s as any).lon ?? (s as any).lng ?? (s as any).longitude);
            if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon] as [number, number];
        }
        if (ride?.course?.path && ride.course.path.length > 0) return ride.course.path[0];
        return null;
    }, [ride?.course?.start, ride?.course?.path]);

    const finishCoords = useMemo(() => {
        const f = ride?.course?.finish;
        if (f && Array.isArray(f) && f.length >= 2 && (f[0] !== 0 || f[1] !== 0)) return [Number(f[0]), Number(f[1])] as [number, number];
        if (f && typeof f === 'object') {
            const lat = Number((f as any).lat ?? (f as any).latitude);
            const lon = Number((f as any).lon ?? (f as any).lng ?? (f as any).longitude);
            if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon] as [number, number];
        }
        if (ride?.course?.path && ride.course.path.length > 0) return ride.course.path[ride.course.path.length - 1];
        return null;
    }, [ride?.course?.finish, ride?.course?.path]);

    const isLoopRide = useMemo(() => {
        if (!meetupCoords || !finishCoords) return false;
        return Math.abs(meetupCoords[0] - finishCoords[0]) < 0.0005 && Math.abs(meetupCoords[1] - finishCoords[1]) < 0.0005;
    }, [meetupCoords, finishCoords]);

    // If an existing ride was saved with a personal approach commute (> 1.5km from meetup),
    // calculate the actual group ride loop metrics dynamically as a fallback
    const trimmedFallback = useMemo(() => {
        if (!ride?.course?.path || !meetupCoords || !finishCoords) return null;
        const distFromPathStart = getDistanceMeters(meetupCoords, ride.course.path[0]);
        if (distFromPathStart > 1500) {
            return trimRouteSegment(ride.course.path, meetupCoords, finishCoords);
        }
        return null;
    }, [ride?.course?.path, meetupCoords, finishCoords]);

    const displayDist = useMemo(() => {
        if (trimmedFallback) return trimmedFallback.distanceMiles;
        if (!gr && !ride) return null;
        if (gr?.distanceMiles && gr.distanceMiles > 0) return gr.distanceMiles;
        if (ride?.course?.path && ride.course.path.length > 1) {
            let meters = 0;
            for (let i = 0; i < ride.course.path.length - 1; i++) {
                const p1 = ride.course.path[i];
                const p2 = ride.course.path[i + 1];
                meters += getDistanceMeters(p1, p2);
            }
            return Number((meters * 0.000621371).toFixed(1));
        }
        return null;
    }, [gr, ride, trimmedFallback]);

    const displayElev = useMemo(() => {
        if (trimmedFallback && trimmedFallback.elevationGainFt !== undefined) return trimmedFallback.elevationGainFt;
        if (!gr) return null;
        if (gr?.elevationGainFt && gr.elevationGainFt > 0) return gr.elevationGainFt;
        if (gr?.elevationProfile && gr.elevationProfile.length > 1) {
            let gain = 0;
            for (let i = 1; i < gr.elevationProfile.length; i++) {
                const diff = gr.elevationProfile[i].elevationFt - gr.elevationProfile[i - 1].elevationFt;
                if (diff > 0) gain += diff;
            }
            return Math.round(gain);
        }
        return null;
    }, [gr, trimmedFallback]);

    const effectiveProfile = useMemo(() => {
        if (trimmedFallback && trimmedFallback.elevationProfile && trimmedFallback.elevationProfile.length > 0) {
            return trimmedFallback.elevationProfile;
        }
        return gr?.elevationProfile;
    }, [trimmedFallback, gr?.elevationProfile]);

    const sectorStats = useMemo(
        () => computeSectorStats(trimmedFallback?.trimmedPath || ride?.course?.path, effectiveProfile, gr?.spiritedSegment),
        [ride, gr, trimmedFallback, effectiveProfile]
    );

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin h-10 w-10 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-400 text-sm">Loading Group Ride...</span>
                </div>
            </div>
        );
    }

    if (!ride) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
                <div className="bg-gray-800 p-8 rounded-xl border border-gray-700 text-center max-w-md">
                    <h2 className="text-2xl font-bold text-red-400 mb-2">Group Ride Not Found</h2>
                    <p className="text-gray-400 text-sm mb-6">The ride you are looking for doesn't exist or was removed.</p>
                    <button onClick={handleBack} className="px-5 py-2.5 bg-cyan-500 text-gray-950 font-bold rounded-lg hover:bg-cyan-400 transition-colors">
                        {backLabel === 'Back' ? 'Back to Events' : backLabel}
                    </button>
                </div>
            </div>
        );
    }

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = gr?.dayOfWeek !== undefined ? days[gr.dayOfWeek] : 'Weekly';

    // GPX Download Handler
    const handleDownloadGpx = () => {
        if (!ride.course?.path || ride.course.path.length === 0) {
            alert('No GPX route data available for this ride.');
            return;
        }

        const trkpts = ride.course.path.map(pt => `<trkpt lat="${pt[0]}" lon="${pt[1]}"></trkpt>`).join('\n        ');
        const gpxData = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="RaceLineIQ" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${ride.name}</name>
    <trkseg>
        ${trkpts}
    </trkseg>
  </trk>
</gpx>`;

        const blob = new Blob([gpxData], { type: 'application/gpx+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${ride.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.gpx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleUploadRide = async (file: File) => {
        if (!currentUser) {
            setUploadStatus({ message: 'Please sign in to upload your ride.', type: 'error' });
            return;
        }
        try {
            const result = await storageService.uploadPendingRaceFile(file, currentUser.uid, setUploadStatus);
            if (result.raceId) {
                navigate(`/race/${result.raceId}/category/${result.categoryId || 'group-ride'}`);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : '';
            const isNoMatch = /could not match|no races found/i.test(message);
            setUploadStatus({
                message: isNoMatch
                    ? `We couldn't match this file to ${ride.name}. Check it was recorded on a scheduled ${dayName} ride near the meetup location.`
                    : message || 'Upload failed. Please try again.',
                type: 'error'
            });
        }
    };

    const formatOccurrenceDate = (dateStr?: string) => {
        if (!dateStr) return '';
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white pb-16">
            <SEO title={`${ride.name} | Group Ride | RaceLineIQ`} description={`View route, schedule, and details for ${ride.name}`} />

            {/* Hero Header */}
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-800 pt-8 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <button onClick={handleBack} className="text-xs font-semibold text-gray-400 hover:text-cyan-400 mb-6 flex items-center gap-1 transition-colors">
                        ← {backLabel}
                    </button>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>

                            <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{ride.name}</h1>
                            {gr?.locationName && (
                                <p className="text-gray-400 text-sm mt-2 flex flex-wrap items-center gap-1.5">
                                    <MapPinIcon className="w-4 h-4 text-cyan-400" />
                                    <span>{gr.locationName}</span>
                                    {meetupCoords && (
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${meetupCoords[0]},${meetupCoords[1]}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline ml-1 inline-flex items-center gap-1 font-medium"
                                            title="Open Meetup in Google Maps"
                                        >
                                            <span>(Directions ↗)</span>
                                        </a>
                                    )}
                                </p>
                            )}

                            {/* Host Info Header */}
                            {(gr?.hostName || gr?.hostUrl) && (
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                                    <span className="text-gray-400">Hosted by:</span>
                                    <span className="font-bold text-white">
                                        {gr?.hostName || 'Community Organized'}
                                    </span>
                                    {gr?.hostUrl && (
                                        <a
                                            href={gr.hostUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-cyan-300 bg-cyan-950/70 border border-cyan-500/40 rounded-md hover:bg-cyan-900/80 hover:text-cyan-200 transition-colors shadow-sm ml-1"
                                        >
                                            <span>🌐 Visit Website</span>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={async () => {
                                    if (!currentUser) {
                                        navigate(`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`);
                                        return;
                                    }
                                    if (!ride || isFavoriting) return;
                                    setIsFavoriting(true);
                                    // Optimistic update
                                    const next = !isFavorited;
                                    setIsFavorited(next);
                                    try {
                                        if (next) {
                                            await userService.addFavoriteGroupRide(currentUser.uid, {
                                                id: ride.slug || ride.id,
                                                name: ride.name,
                                                locationName: ride.groupRideDetails?.locationName,
                                                savedAt: Date.now()
                                            });
                                        } else {
                                            await userService.removeFavoriteGroupRide(currentUser.uid, ride.slug || ride.id);
                                        }
                                    } catch (err) {
                                        console.error('Failed to update favorite state', err);
                                        setIsFavorited(!next); // Revert on error
                                    } finally {
                                        setIsFavoriting(false);
                                    }
                                }}
                                disabled={isFavoriting}
                                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 border ${
                                    isFavorited
                                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
                                        : 'bg-gray-800 text-gray-300 border-gray-700 hover:text-white hover:border-gray-600'
                                } disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                                {isFavoriting ? '⏳ Saving...' : isFavorited ? '★ Favorited' : '☆ Favorite Ride'}
                            </button>
                            <button
                                onClick={handleDownloadGpx}
                                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-cyan-600/20"
                            >
                                📥 Download GPX
                            </button>
                            <button
                                onClick={handleShare}
                                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold text-sm rounded-lg transition-colors border border-gray-700 flex items-center gap-2"
                            >
                                {copiedLink ? '✓ Copied Link' : '🔗 Share'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left Column (2 Cols): Details, Map, Elevation */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Ride Details & Info Tabs (Moved above map; Regroup tab removed) */}
                    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
                        <div className="flex border-b border-gray-700 bg-gray-800/80">
                            <button
                                onClick={() => setActiveTab('about')}
                                className={`flex-1 py-3 px-4 text-sm font-bold text-center border-b-2 transition-colors ${activeTab === 'about' ? 'border-cyan-400 text-cyan-400 bg-gray-700/40' : 'border-transparent text-gray-400 hover:text-white'}`}
                            >
                                About & History
                            </button>
                            <button
                                onClick={() => setActiveTab('host')}
                                className={`flex-1 py-3 px-4 text-sm font-bold text-center border-b-2 transition-colors ${activeTab === 'host' ? 'border-cyan-400 text-cyan-400 bg-gray-700/40' : 'border-transparent text-gray-400 hover:text-white'}`}
                            >
                                Host & Sponsor
                            </button>
                        </div>

                        <div className="p-6">
                            {activeTab === 'about' && (
                                <div className="space-y-4 text-sm text-gray-300 leading-relaxed">
                                    <p>{gr?.infoText || 'No detailed write-up has been added for this ride yet. Check meetup time and location details above.'}</p>
                                </div>
                            )}

                            {activeTab === 'host' && (
                                <div className="space-y-3 text-sm">
                                    <div className="bg-gray-900/60 p-5 rounded-lg border border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div>
                                            <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider block mb-1">Group Ride Host</span>
                                            <h4 className="font-bold text-lg text-white">{gr?.hostName || 'Community Organized'}</h4>
                                        </div>
                                        {gr?.hostUrl && (
                                            <a
                                                href={gr.hostUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 border border-cyan-500/40 rounded-lg text-xs font-bold transition-colors w-fit"
                                            >
                                                <span>Visit Host Website</span>
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Route Preview Container (Interactive Leaflet Map) */}
                    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-6 shadow-xl space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-700 pb-3">
                            <h2 className="text-lg font-bold text-cyan-400 flex items-center gap-2">
                                Route
                            </h2>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs font-medium text-gray-400">
                                <span className="flex items-center gap-1.5">
                                    <span className="w-3 h-3 rounded-full bg-indigo-600"></span> Route
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className={`w-3 h-3 rounded-full ${isLoopRide ? 'bg-purple-500' : 'bg-emerald-500'}`}></span> {isLoopRide ? 'Start/Finish' : 'Meetup'}
                                </span>
                                {!isLoopRide && finishCoords && (
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded-full bg-red-500"></span> Ride End
                                    </span>
                                )}
                                {gr?.spiritedSegment && (
                                    <span className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded-full bg-cyan-500"></span> Race Sector
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Leaflet Route Map */}
                        <GroupRideMap
                            course={ride.course}
                            spiritedSegment={gr?.spiritedSegment}
                            locationName={gr?.locationName}
                            height="400px"
                        />
                    </div>

                    {/* Elevation Profile Chart */}
                    <GroupRideElevationChart
                        elevationProfile={effectiveProfile}
                        spiritedSegment={gr?.spiritedSegment}
                        elevationGainFt={displayElev ?? gr?.elevationGainFt}
                    />
                </div>

                {/* Right Column (1 Col) */}
                <div className="space-y-6">
                    {/* Recent Rides (per-date occurrences) + Upload */}
                    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-cyan-400 border-b border-gray-700 pb-2 uppercase tracking-wider text-xs">
                            Recent Rides
                        </h3>

                        {isLoadingOccurrences ? (
                            <p className="text-xs text-gray-500">Loading rides...</p>
                        ) : occurrences.length > 0 ? (
                            <div className="space-y-2">
                                {occurrences.map(occ => {
                                    const userRode = !!currentUser && occ.riderUserIds.includes(currentUser.uid);
                                    return (
                                        <Link
                                            key={occ.event.id}
                                            to={`/race/${occ.event.slug || occ.event.id}/category/${occ.categoryId}`}
                                            className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gray-900/60 border border-gray-700 hover:border-cyan-500/60 transition-colors"
                                        >
                                            <div>
                                                <div className="text-sm font-semibold text-white">{formatOccurrenceDate(occ.event.occurrenceDate)}</div>
                                                <div className="text-xs text-gray-400">{occ.riderCount} {occ.riderCount === 1 ? 'rider' : 'riders'}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {userRode && (
                                                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-950/70 border border-emerald-600/40 px-1.5 py-0.5 rounded">You rode</span>
                                                )}
                                                <span className="text-cyan-400 text-sm">→</span>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-400 leading-relaxed">
                                No rides recorded yet. Riders with Hammerhead or Wahoo connected are added automatically after each ride.
                            </p>
                        )}

                        {currentUser ? (
                            <div className="border-t border-gray-700 pt-4 space-y-2">
                                <p className="text-xs text-gray-400">Upload your file and we'll match it to the right week.</p>
                                {uploadStatus && (
                                    <div className={`p-2.5 rounded-lg text-xs ${uploadStatus.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-800' : uploadStatus.type === 'error' ? 'bg-red-900/50 text-red-300 border border-red-800' : 'bg-cyan-900/50 text-cyan-300 border border-cyan-800'}`}>
                                        {uploadStatus.message}
                                    </div>
                                )}
                                <FileUpload 
                                    onFileUpload={handleUploadRide} 
                                    label="Upload my Ride" 
                                    buttonClassName="px-4 py-2.5 text-sm"
                                />
                            </div>
                        ) : (
                            <div className="border-t border-gray-700 pt-4">
                                <div className="p-4 bg-gradient-to-br from-cyan-950/60 via-gray-900 to-gray-900 border border-cyan-500/40 rounded-xl space-y-3">
                                    <div className="flex items-center gap-2 text-cyan-400">
                                        <span className="text-base">🚴</span>
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-300">Were you on this ride?</h4>
                                    </div>
                                    <p className="text-xs text-gray-300 leading-relaxed">
                                        Create a free account to upload your ride, see your drafting stats, and get added to the group replay.
                                    </p>
                                    <div className="space-y-2 pt-1">
                                        <Link
                                            to={`/auth?mode=signup&returnTo=${encodeURIComponent(window.location.pathname)}`}
                                            className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold rounded-lg text-xs transition-all shadow-md shadow-cyan-500/20 active:scale-95"
                                        >
                                            Create Account to Upload
                                        </Link>
                                        <div className="text-center">
                                            <Link
                                                to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`}
                                                className="text-[11px] text-gray-400 hover:text-cyan-400 transition-colors"
                                            >
                                                Already have an account? <span className="text-cyan-400 font-semibold underline">Sign In</span>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Ride Snapshot Card */}
                    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-cyan-400 border-b border-gray-700 pb-2 uppercase tracking-wider text-xs">
                            Ride Snapshot
                        </h3>
                        <div className="space-y-3 text-sm">
                            {(gr?.hostName || gr?.hostUrl) && (
                                <>
                                    <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                                        <span className="text-gray-400">Host</span>
                                        <span className="font-semibold text-white">{gr?.hostName || 'Community'}</span>
                                    </div>
                                    {gr?.hostUrl && (
                                        <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                                            <span className="text-gray-400">Website</span>
                                            <a
                                                href={gr.hostUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold text-cyan-400 hover:underline text-xs truncate max-w-[150px]"
                                            >
                                                {gr.hostUrl.replace(/^https?:\/\/(www\.)?/, '')} ↗
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Meetup Spot</span>
                                <div className="text-right">
                                    <span className="font-semibold text-white block truncate max-w-[170px]">{gr?.locationName || 'Start Point'}</span>
                                    {meetupCoords && (
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${meetupCoords[0]},${meetupCoords[1]}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] text-cyan-400 hover:underline inline-flex items-center gap-0.5"
                                        >
                                            View on Map ↗
                                        </a>
                                    )}
                                </div>
                            </div>
                            {!isLoopRide && finishCoords && (
                                <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                                    <span className="text-gray-400">Ride End</span>
                                    <div className="text-right">
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${finishCoords[0]},${finishCoords[1]}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[11px] text-red-400 hover:underline inline-flex items-center gap-0.5 font-medium"
                                        >
                                            End Point on Map ↗
                                        </a>
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Distance</span>
                                <span className="font-bold text-cyan-400">{displayDist !== null ? `${displayDist} mi` : 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Elev Gain</span>
                                <span className="font-bold text-emerald-400">{displayElev !== null ? `+${displayElev.toLocaleString()} ft` : 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Recurrence</span>
                                <span className="font-semibold text-white capitalize">{gr?.frequency || 'Weekly'}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Day</span>
                                <span className="font-semibold text-white">{dayName}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Meetup Time</span>
                                <span className="font-semibold text-white">
                                    {formatTime12h(gr?.meetupTime) || 'N/A'}{gr?.timeZone && ` ${getTimeZoneAbbreviation(gr.timeZone)}`}
                                </span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Roll Time</span>
                                <span className="font-semibold text-white">
                                    {formatTime12h(gr?.rollTime) || 'N/A'}{gr?.timeZone && ` ${getTimeZoneAbbreviation(gr.timeZone)}`}
                                </span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-700/50">
                                <span className="text-gray-400">Policy</span>
                                <span className="font-semibold text-white">{gr?.isDrop ? 'Drop' : 'No Drop'}</span>
                            </div>
                            <div className="flex justify-between py-1">
                                <span className="text-gray-400">Skill Level</span>
                                <span className="font-semibold text-white capitalize">{gr?.skillLevel?.replace('_', ' ') || 'All Levels'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Race Sector Card */}
                    {sectorStats && (
                        <div className="bg-cyan-950/40 border border-cyan-700/50 rounded-xl p-6 shadow-xl space-y-4">
                            <h3 className="font-bold text-cyan-300 border-b border-cyan-800/60 pb-2 uppercase tracking-wider text-xs flex items-center gap-1.5">
                                <span>⚡</span> Race Sector
                            </h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between py-1 border-b border-cyan-900/50">
                                    <span className="text-gray-400">Distance</span>
                                    <span className="font-bold text-cyan-400">{sectorStats.distanceMiles !== null ? `${sectorStats.distanceMiles} mi` : 'N/A'}</span>
                                </div>
                                <div className="flex justify-between py-1">
                                    <span className="text-gray-400">Elev Gain</span>
                                    <span className="font-bold text-emerald-400">{sectorStats.elevationGainFt !== null ? `+${sectorStats.elevationGainFt.toLocaleString()} ft` : 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupRideDetailsPage;

